<template>
  <div ref="officeRef" style="height: 100%"></div>
</template>

<script lang="ts" setup>
  import { ref, watch, nextTick, onBeforeUnmount, reactive } from 'vue';
  import type { DocConfig } from './index.ds';
  import LzdUtil, {
    getAppId,
    getDataScopeId,
    JSONUtil
  } from '@/utils/lzd-util';
  import { useUserStore } from '@/store/modules/user';
  import { EleMessage, uuid } from 'ele-admin-plus';
  import { FILE_API_BASE_URL } from '@/config/setting';

  const userStore = useUserStore();

  const props = withDefaults(
    defineProps<{
      /** 文档 url 地址 */
      modelValue: string;
      /** 模式: edit 修改、preview 预览、comment 批注 */
      mode?: 'edit' | 'preview' | 'comment';
      /** 文档名称 */
      fileName?: string;
      /** 文档类型 */
      fileType?: 'docx' | 'doc' | 'ppt' | 'pptx' | 'xls' | 'xlsx' | 'pdf';
      /** 保存地址 */
      saveUrl?: string;
      /** 文档ID 用于保存数据 */
      docId?: string;
    }>(),
    {
      mode: 'preview',
      fileType: 'docx',
      fileName: 'Preview Document ',
      saveUrl: `${FILE_API_BASE_URL}/bsl/file/document/save/by/onlyoffice`
    }
  );

  /** 保存所需的参数 */
  const saveParam = reactive({
    docId: props.docId,
    appId: getAppId(),
    dataScopeId: getDataScopeId()
  });

  const officeRef = ref<HTMLDivElement | null>(null);
  let docEditor: any = null;

  const initEditor = () => {
    if (!officeRef.value) return;
    if (props.mode === 'edit' && !props.saveUrl) {
      EleMessage.error('office组件不能进行编辑，请传递回调地址');
      return;
    }
    // 保证编辑后查看是编辑后的文档
    let key = '';
    if (props.mode === 'edit') {
      if (!saveParam.docId) {
        EleMessage.error('保存文档地址Id不存在');
        return;
      }
      key = JSONUtil.toStr(saveParam);
    }

    let callbackUrl = LzdUtil.buildFileLink(props.saveUrl);
    const docConfig: DocConfig = {
      document: {
        fileType: props.fileType,
        key,
        title: props.fileName,
        permissions: {
          edit: props.mode === 'edit',
          print: false,
          download: true,
          chat: false,
          // 控制批注
          comment: true
        },
        // url: props.modelValue + `?access_token=${getToken()}`
        url: LzdUtil.buildFileLink(props.modelValue)!
      },
      editorConfig: {
        callbackUrl,
        coEditing: {
          mode: 'fast',
          change: false
        },
        lang: 'zh-CN',
        customization: {
          anonymous: {
            request: false,
            label: 'Guest'
          },
          about: false,
          autosave: false,
          comments: true,
          help: false,
          compactToolbar: false,
          compactHeader: true,
          compatibleFeatures: true,
          hideRightMenu: true,
          plugins: false,
          zoom: 90
        },
        user: {
          id: userStore.info!.userid!,
          image: userStore.info?.sign ?? '',
          name: userStore.info!.username!
        }
      }
    };
    // 先销毁
    destroyInst();
    const officeInstDiv = document.createElement('div');
    officeInstDiv.id = 'office-inst-' + uuid(16);
    officeInstDiv.style.height = '100%';
    officeRef.value.appendChild(officeInstDiv);
    docEditor = new globalThis.DocsAPI.DocEditor(officeInstDiv.id, docConfig);
  };

  /** 销毁实例 */
  const destroyInst = () => {
    docEditor?.destroyEditor();
    docEditor = null;
    while (officeRef.value?.firstChild) {
      officeRef.value.removeChild(officeRef.value.firstChild);
    }
  };

  onBeforeUnmount(() => {
    destroyInst();
  });

  watch(
    () => [props.modelValue, props.mode],
    (_) => {
      nextTick(() => {
        nextTick(() => {
          initEditor();
        });
      });
    },
    { immediate: true }
  );
</script>

<script lang="ts">
  export default {
    name: 'LzdOffice'
  };
</script>
